import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import SvgIcon from '@home/components/SvgIcon';
import { useVideoDownload } from '../../hooks/useVideoDownload';
import LoadingSpinner from './LoadingSpinner';
import IconVideoPlay from '@opm/assets/svg/icon-video-play.svg';
import { Icon } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_TM, DBO_V } from '@op/utils/format';
import { DBOM_CONTENT } from '@opm/utils';
import ChannelLabel from '@op/components/ChannelLabel';
import URLShare from '@opm/components/URLShare';
import { LanguageType } from '@op/types/common';
import { IVideoItemData } from '../../HashtagTable';

interface IVideoItemProps {
  videoId: string;
  videoImage: string;
  index: number;
  autoPlay?: boolean;
  videoInfo?: IVideoItemData;
  language?: LanguageType;
  isPlaying?: boolean;
  onPlay?: (videoId: string) => void;
}

function VideoItem({
  videoId,
  videoImage,
  index: _index,
  autoPlay = false,
  videoInfo,
  language = 'origin',
  isPlaying,
  onPlay,
}: IVideoItemProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { formatMessage } = useLanguage();
  const {
    videoUrls,
    loading,
    activated,
    retryExhausted,
    fetchVideoUrl,
    retry,
  } = useVideoDownload(videoId);

  const [currentUrlIndex, setCurrentUrlIndex] = useState<number>(0);
  const [allVideosFailed, setAllVideosFailed] = useState<boolean>(false);
  const [showPlayButton, setShowPlayButton] = useState<boolean>(false);

  // 当 videoUrls 变化时重置索引和失败状态
  useEffect(() => {
    setCurrentUrlIndex(0);
    setAllVideosFailed(false);
  }, [videoUrls]);

  // autoPlay 时自动触发请求
  useEffect(() => {
    if (autoPlay) {
      fetchVideoUrl();
    }
  }, [autoPlay]);

  // 监听 isPlaying 变化，控制其他视频暂停
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // 当 isPlaying 明确为 false（其他视频开始播放）且当前视频正在播放时，暂停当前视频
    if (isPlaying === false && !video.paused) {
      video.pause();
      setShowPlayButton(true);
    }
  }, [isPlaying]);

  const currentVideoUrl = videoUrls[currentUrlIndex] || '';

  // 处理视频加载错误，尝试下一个链接
  const handleVideoError = useCallback((): void => {
    if (currentUrlIndex < videoUrls.length - 1) {
      setCurrentUrlIndex(prev => prev + 1);
    } else {
      // 所有链接都尝试过了
      setAllVideosFailed(true);
    }
  }, [currentUrlIndex, videoUrls.length]);

  // 视频播放事件
  const handlePlay = (): void => {
    setShowPlayButton(false);
    // 通知父组件当前视频开始播放
    onPlay?.(videoId);
  };

  // 视频暂停事件
  const handlePause = (): void => {
    setShowPlayButton(true);
  };

  // 点击播放按钮，恢复播放
  const handleResumePlay = (): void => {
    if (videoRef.current) {
      const playPromise = videoRef.current.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            handlePlay();
          })
          .catch((error) => {
            console.error('Video play failed:', error);
            setShowPlayButton(true);
          });
      }
    }
  };

  // 自动播放视频（激活后获取到链接时）
  useEffect(() => {
    if (activated && currentVideoUrl && videoRef.current) {
      const playPromise = videoRef.current.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            handlePlay();
          })
          .catch((err) => {
            console.warn('Autoplay was prevented:', err);
            setShowPlayButton(true);
          });
      }
    }
  }, [activated, currentVideoUrl]);

  // 点击封面，触发请求或重试
  const handleClickCover = (): void => {
    if (retryExhausted) {
      retry();
    } else {
      fetchVideoUrl();
    }
  };

  // 渲染统计信息栏
  const renderVideoStats = (): React.ReactNode => {
    if (!videoInfo) return null;
    return (
      <div className="dbom-hashtag-video-stats">
        <div className="dbom-hashtag-video-stats-item">
          <span className="dbom-hashtag-stat-icon --playNum"></span>
          <span>{DBO_V.reviews(videoInfo.tweets_view)}</span>
        </div>
        <div className="dbom-hashtag-video-stats-item">
          <span className="dbom-hashtag-stat-icon --likes"></span>
          <span>{DBO_V.reviews(videoInfo.tweets_like)}</span>
        </div>
        <div className="dbom-hashtag-video-stats-item">
          <span className="dbom-hashtag-stat-icon --duration"></span>
          <span>{DBO_TM.duration(videoInfo.video_duration)}</span>
        </div>
      </div>
    );
  };

  // 渲染分享按钮
  const renderShareButton = (): React.ReactNode => {
    if (!videoInfo?.video_url) return null;
    return (
      <URLShare
        url={videoInfo.video_url}
        className="dbom-hashtag-video-share"
        iconClassName="dbom-hashtag-video-share-svg"
      />
    );
  };

  // 渲染视频信息区域
  const renderVideoInfo = useMemo((): React.ReactNode => {
    if (!videoInfo) return null;
    return (
      <div className={classnames("dbom-hashtag-video-info", { '--active': activated })}>
        <ChannelLabel
          className="dbom-hashtag-video-info-username"
          channel_name={videoInfo.channel_name}
          replaceText={videoInfo.anchor_name}
        />
        <div className="dbom-hashtag-video-info-date">
          {moment(videoInfo.video_release_time).format('YYYY-MM-DD HH:mm')}
        </div>
        <div className="dbom-hashtag-video-info-title">
          {DBOM_CONTENT.videoTitle(language, videoInfo)}
        </div>
      </div>
    );
  }, [activated]);

  const renderContent = useCallback(() => {
    return (
      <video
        ref={videoRef}
        className="dbom-hashtag-video-player"
        src={currentVideoUrl}
        poster={allVideosFailed ? undefined : videoImage}
        controls
        playsInline
        preload="metadata"
        onError={handleVideoError}
        onPlay={handlePlay}
        onPause={handlePause}
      />
    );
  }, [currentVideoUrl, videoImage, handleVideoError, allVideosFailed]);

  // 未激活或加载中状态：显示封面图 + 播放按钮/loading/刷新
  if (!activated || loading) {
    const renderIcon = () => {
      if (loading) {
        return <LoadingSpinner size={85} className="dbom-hashtag-video-loading-icon" strokeWidth={0.7} />;
      }
      if (retryExhausted) {
        return <SvgIcon type="shuaxin" className="dbom-hashtag-video-retry-icon" />;
      }
      return <img src={IconVideoPlay} className="dbom-hashtag-video-play-icon" />;
    };

    return (
      <div className="dbom-hashtag-video-item">
        {renderVideoStats()}
        {renderShareButton()}
        {renderVideoInfo}
        <div
          className={classnames('dbom-hashtag-video-cover', {
            '--loading': loading,
          })}
          onClick={!loading ? handleClickCover : undefined}
        >
          <img
            className="dbom-hashtag-video-cover-img"
            src={videoImage}
            alt="video cover"
          />
          {renderIcon()}
        </div>
      </div>
    );
  }

  return (
    <div className="dbom-hashtag-video-item">
      {renderVideoStats()}
      {renderShareButton()}
      {renderVideoInfo}
      <div
        className={classnames(
          'dbom-hashtag-video-player-wrapper',
          { '--black': !!currentVideoUrl && !allVideosFailed }
        )}
      >
        {renderContent()}
        {showPlayButton && activated && (
          <div
            className="dbom-hashtag-video-play-overlay"
            onClick={handleResumePlay}
            role="button"
            aria-label="播放视频"
          >
            <img
              src={IconVideoPlay}
              className="dbom-hashtag-video-play-icon"
              alt="Play"
            />
          </div>
        )}
        {allVideosFailed && (
          <div className="dbom-hashtag-video-error-overlay">
            <Icon type="error" className="dbom-hashtag-video-error-icon" />
            <span className="dbom-hashtag-video-error-text">{formatMessage('The video cannot be played.')}</span>

          </div>
        )}
      </div>
    </div>
  );
}

export default VideoItem;